/**
 * Everything a person has to tell two registered keys apart, and nothing
 * more: the name they typed and the last four characters. The key material
 * itself never reaches this component -- there is no field on `OpsKey` it
 * could occupy (`src/ops/api/schemas.ts`), and `api`'s `KeyOut` carries no
 * fingerprint either (`659:19979` draws name and last four alone).
 *
 * The name reads in sans beside a key glyph; the last four read in
 * monospace behind a mask, marked `data-mono`.
 *
 * The activation date is optional and separate from those two: it answers
 * "since when", not "which one", so a caller with no use for it (a bare
 * identity in a form's own success state) omits the prop rather than
 * getting a third fact it never asked for.
 */

import type { ReactNode } from 'react';
import { KeyRound } from 'lucide-react';

import { Absent } from '../../chrome/measure';
import { defineCatalogue, getLocale } from '../../i18n';
import { formatDate } from '../lib/date';

const catalogue = defineCatalogue(
  {
    pending: 'Ainda não ativada.',
  },
  {
    pending: 'Not yet activated.',
  },
);

export function KeyIdentity(props: {
  name: string;
  lastFour: string;
  /** Present shows the third fact; a `null` value renders as pending
   *  rather than a bare dash. */
  activatedAt?: string | null;
}): ReactNode {
  return (
    <span className="inline-flex flex-wrap items-center gap-3 text-body-sm">
      <span className="inline-flex items-center gap-2">
        <KeyRound aria-hidden className="size-4 text-muted-foreground" />
        <span data-testid="key-name">{props.name}</span>
      </span>
      <span
        data-testid="key-last-four"
        data-mono="true"
        className="font-mono text-muted-foreground"
      >
        {`${'\u2022'.repeat(4)}${props.lastFour}`}
      </span>
      {props.activatedAt !== undefined && (
        <span data-testid="key-activated" data-mono="true">
          {props.activatedAt !== null ? (
            formatDate(props.activatedAt, getLocale())
          ) : (
            <Absent reason={{ text: catalogue.t('pending') }} />
          )}
        </span>
      )}
    </span>
  );
}
